import { useMemo } from 'react';

import { routeTree } from 'src/routes';

import { useAccessContext } from '@/routing/useAccessContext';
import { canAccessAppRouteMeta } from '@/routing/access-meta';
import { extractNavEntriesFromRouteTree } from '@/routing/nav-from-router';
import { useNavEntryLabel } from '@/routing/use-nav-entry-label';
import {
  ORGANIZATION_SETTINGS_SECTIONS,
  ORGANIZATION_SETTINGS_GROUPS,
  sectionPath,
  type OrganizationSettingsSection,
} from '@/pages/authenticated/settings/organization/organizationSettingsSections';
import type { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { getPermissionKey } from '@/hooks/authz/useRoutePermissions';

export type QuickSearchPage = {
  id: string;
  label: string;
  sublabel?: string;
  path: string;
  kind: 'page' | 'setting';
};

export function useQuickSearchPages(search: string): QuickSearchPage[] {
  const accessCtx = useAccessContext();
  const navEntryLabel = useNavEntryLabel();

  const allPages = useMemo(() => {
    const pages: QuickSearchPage[] = [];

    const navEntries = extractNavEntriesFromRouteTree(routeTree).filter((e) =>
      canAccessAppRouteMeta(e.meta, accessCtx),
    );

    for (const entry of navEntries) {
      pages.push({
        id: `page:${entry.id}`,
        label: navEntryLabel(entry),
        sublabel: entry.meta.nav?.group,
        path: entry.fullPath,
        kind: 'page',
      });
    }

    // The /feedback route has no `staticData.app.nav` (kept out of the sidebar),
    // so it isn't picked up by the nav-derived loop above. Add it explicitly so
    // it's still reachable via Quick Find. Available to all authenticated users.
    pages.push({
      id: 'page:feedback',
      label: 'My Feedback',
      sublabel: 'Feedback',
      path: '/feedback',
      kind: 'page',
    });

    const settingSections = filterAccessibleSettings(
      ORGANIZATION_SETTINGS_SECTIONS,
      accessCtx.isAdmin,
      accessCtx.isModuleEnabled,
      accessCtx.permissions,
    );

    for (const section of settingSections) {
      const group = ORGANIZATION_SETTINGS_GROUPS.find(
        (g) => g.id === section.group,
      );
      pages.push({
        id: `setting:${section.id}`,
        label: section.title,
        sublabel: group ? `Settings · ${group.title}` : 'Settings',
        path: sectionPath(section.id),
        kind: 'setting',
      });
    }

    return pages;
  }, [accessCtx, navEntryLabel]);

  return useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];

    return allPages.filter(
      (p) =>
        p.label.toLowerCase().includes(q) ||
        (p.sublabel?.toLowerCase().includes(q) ?? false),
    );
  }, [allPages, search]);
}

function filterAccessibleSettings(
  sections: readonly OrganizationSettingsSection[],
  isAdmin: boolean,
  isModuleEnabled: (module: ProductModule) => boolean,
  permissions: Map<string, boolean>,
): OrganizationSettingsSection[] {
  return sections.filter((section) => {
    if (section.gentlyOnly && !isAdmin) return false;
    if (section.requiredModule && !isModuleEnabled(section.requiredModule))
      return false;
    if (section.requiredPermission && !isAdmin) {
      const key = getPermissionKey(
        section.requiredPermission.action,
        section.requiredPermission.subject,
      );
      if (!permissions.get(key)) return false;
    }
    return true;
  });
}
